import { Injectable } from '@angular/core';
import { spreadObjectStore$ } from '@sama/app-state';
import { assert, isDefined } from '@sama/js-core';
import { combineLatest, mergeAll } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  share,
  switchMap,
} from 'rxjs/operators';
import { Mesh } from 'three';
import { assertStore } from '../../cross-team-shared/misc-utils/assert-store';
import { assertDefined } from '../../cross-team-shared/misc-utils/strict-record';
import { AbstractSensorContentStore } from '../../cross-team-shared/state/abstract-sensor-content-store';
import { Axis } from '../renderers/edit-panel/edit-panel.component';
import {
  ensureKeyLocation,
  interpolateLocation,
} from '../utils/point-cloud-interpolation-helper';
import { eulerToPoint, vectorToPoint } from '../utils/point-cloud-utils';
import { CuboidState3D } from './cuboid-state-3d';
import {
  getFocusedCuboidLocation,
  getFocusedShapeIndex,
  getVisibleFocusedCuboidLocation,
  isFocusedCuboidVisible,
} from './focused-shape-utils';
import { PointCloudContentState } from './point-cloud-content-state';
import { SensorFrame3D } from './sensor-renderer-state';
import { getViewRegionNameForAxis } from './view-region-state';

@Injectable()
export class PointCloudContentStore extends AbstractSensorContentStore<
  PointCloudContentState,
  CuboidState3D,
  SensorFrame3D
> {
  // Emits individual cuboid states when any of them changes
  shape$ = spreadObjectStore$(this('render')('cuboids')).pipe(
    switchMap((shapeStores) => shapeStores.map((shapeStore) => shapeStore.$)),
    mergeAll(),
    filter(isDefined),
    share(),
  );

  focusedShapeLocation$ = combineLatest([
    this.focusedShape$,
    this('render')('frameNumber').$,
  ]).pipe(map(() => getFocusedCuboidLocation(this.state())));

  focusedShapeVisible$ = combineLatest([
    this('render')('viewMode').$,
    this.focusedShape$,
    this('render')('frameNumber').$,
    this('render')('shapeFilters').$,
  ]).pipe(
    map(() => isFocusedCuboidVisible(this.state())),
    distinctUntilChanged(),
  );

  location$ = combineLatest([
    this('locations').$,
    this('render')('frameNumber').$,
  ]).pipe(
    map(([locations, frameNumber]) =>
      locations ? locations[frameNumber] : undefined,
    ),
  );

  visibleFocusedCuboidLocation$ = combineLatest([
    this.focusedShape$,
    this('render')('frameNumber').$,
  ]).pipe(map(() => getVisibleFocusedCuboidLocation(this.state())));

  constructor(initialState: PointCloudContentState) {
    super(initialState, 'PointCloudContentStore');
  }

  getLayerSettingsStore() {
    return this('render')('layerSettings');
  }

  getFocusedShapeStore() {
    const index = this.getFocusedShapeIndex();
    if (!index) {
      return undefined;
    }
    // Because of a delay between the core / content teams shape
    // synchronization, the focused shape index can sometimes refer
    // to a non-existing shape.
    // fix me if you change this code
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- fix me!
    if (!this.state().render.cuboids[index]) {
      return undefined;
    }
    return this('render')('cuboids')(index);
  }

  getFocusedShapeIndex() {
    return getFocusedShapeIndex(this.state());
  }

  getFocusedShapeLocation() {
    const focusedCuboidStore = this.getFocusedShapeStore();
    assert(focusedCuboidStore, 'No focused cuboid found');

    const { frameNumber } = this.state().render;
    return interpolateLocation(
      focusedCuboidStore.state().key_locations,
      frameNumber,
    );
  }

  getMaxFrameNumber() {
    return this.state().sensorRendererState.sensorFrames.length;
  }

  updateFocusedLocationFromCuboidMesh(cuboid: Mesh) {
    // ensures cuboid is not updated by ensureKeyLocation
    const cuboidClone = cuboid.clone();
    const cuboidStore = this.getFocusedShapeStore();
    assert(cuboidStore, 'only call this when there is a focused cuboid');

    const frameNumber = this.state().render.frameNumber;
    ensureKeyLocation(cuboidStore('key_locations'), frameNumber).assign({
      position: vectorToPoint(cuboidClone.position),
      rotation: eulerToPoint(cuboidClone.rotation),
      scale: vectorToPoint(cuboidClone.scale),
    });
  }

  getFocusedCuboidLocationStore() {
    const focusedCuboidStore = this.getFocusedShapeStore();
    assertDefined(focusedCuboidStore);

    return ensureKeyLocation(
      focusedCuboidStore('key_locations'),
      this.state().render.frameNumber,
    );
  }

  getViewRegionStore(axis: Axis) {
    const viewRegionName = getViewRegionNameForAxis(axis);
    const viewRegionStore = this('viewRegions')('regions')(viewRegionName);
    assertStore(viewRegionStore, 'edit panels must always have a view region');
    return viewRegionStore;
  }

  protected buildFocusedShapeStore$() {
    return this('render')('selection').$.pipe(
      map(() => this.getFocusedShapeStore()),
    );
  }

  protected buildCurrentSensorFrame$() {
    return this('render')('frameNumber').$.pipe(
      switchMap(
        (frameNumber) =>
          this('sensorRendererState')('sensorFrames')(frameNumber).$,
      ),
    );
  }
}
